import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { usePageTransition, SUNSARI_LOGO_URL } from '../PageTransition';

export const MainNavigation = () => {
  const { t } = useTranslation();
  const { navigateWithTransition } = usePageTransition();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { name: t('nav.menu'), path: '/menu' },
    { name: t('nav.lunch'), path: '/lunch-menu' },
    { name: 'ORDER', path: '/order' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.catering'), path: '/catering' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  return (
    <motion.nav 
      className="fixed top-8 md:top-9 left-0 right-0 z-[9999] bg-[#F5F3EF]/98 backdrop-blur-xl border-b border-[#2F2F2F]/10"
      initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="max-w-[1800px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-14 md:h-16">
          <motion.div className="cursor-pointer"
            onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigateWithTransition('/'); }}
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }} data-testid="nav-logo">
            <img src={SUNSARI_LOGO_URL} alt="Sunsari" className="h-12 md:h-14 w-auto" />
          </motion.div>
          
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.button key={item.name} onClick={() => navigateWithTransition(item.path)}
                className="relative text-[#2F2F2F]/70 hover:text-[#2F2F2F] text-[11px] tracking-[0.2em] uppercase font-light py-2 group"
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + index * 0.1 }} whileHover={{ y: -2 }}>
                {item.name}
                <motion.span className="absolute bottom-0 left-0 h-[1px] bg-[#2F2F2F]" initial={{ width: 0 }} whileHover={{ width: '100%' }} transition={{ duration: 0.3 }} />
              </motion.button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <motion.button onClick={() => navigateWithTransition('/order')}
              className="bg-[#C97A2B] text-white px-5 lg:px-6 py-2.5 text-[10px] lg:text-[11px] tracking-[0.15em] uppercase font-medium hover:bg-[#B65A3A] transition-colors"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }} data-testid="nav-order-btn">
              Order Online
            </motion.button>
            <motion.button onClick={() => navigateWithTransition('/combine-tables')}
              className="bg-[#2F2F2F] text-white px-5 lg:px-6 py-2.5 text-[10px] lg:text-[11px] tracking-[0.15em] uppercase font-medium hover:bg-[#C97A2B] transition-colors"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }} data-testid="nav-reserve-btn">
              {t('nav.reserve')}
            </motion.button>
          </div>

          <button className="lg:hidden flex flex-col gap-1.5 p-2 ml-2" onClick={() => setIsMenuOpen(!isMenuOpen)} data-testid="mobile-menu-btn">
            <motion.div className="w-5 h-[1.5px] bg-[#2F2F2F]" animate={{ rotate: isMenuOpen ? 45 : 0, y: isMenuOpen ? 5 : 0 }} />
            <motion.div className="w-5 h-[1.5px] bg-[#2F2F2F]" animate={{ opacity: isMenuOpen ? 0 : 1 }} />
            <motion.div className="w-5 h-[1.5px] bg-[#2F2F2F]" animate={{ rotate: isMenuOpen ? -45 : 0, y: isMenuOpen ? -5 : 0 }} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-[#F5F3EF] border-t border-[#2F2F2F]/10">
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item, index) => (
                <motion.button key={item.name} onClick={() => { navigateWithTransition(item.path); setIsMenuOpen(false); }}
                  className="block w-full text-left text-[#2F2F2F]/80 text-sm tracking-[0.1em] uppercase py-2 border-b border-[#2F2F2F]/10"
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }}>
                  {item.name}
                </motion.button>
              ))}
              <div className="flex gap-3 pt-3">
                <motion.button onClick={() => { navigateWithTransition('/order'); setIsMenuOpen(false); }}
                  className="flex-1 bg-[#C97A2B] text-white py-3 text-sm tracking-[0.1em] uppercase font-medium text-center"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} data-testid="mobile-order-btn">
                  Order Online
                </motion.button>
                <motion.button onClick={() => { navigateWithTransition('/combine-tables'); setIsMenuOpen(false); }}
                  className="flex-1 bg-[#2F2F2F] text-white py-3 text-sm tracking-[0.1em] uppercase font-medium text-center"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} data-testid="mobile-reserve-btn">
                  {t('nav.reserve')}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default MainNavigation;
